import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, Building2, Star } from "lucide-react";
import type { Branch } from "@/lib/api/branches";
import { cn } from "@/lib/utils";

interface BranchSubNavProps {
  branch: Branch | null;
}

const navItems = [
  { label: "Overview", path: "" },
  { label: "Staff", path: "/staff" },
  { label: "Hours", path: "/hours" },
  { label: "Settings", path: "/settings" },
  { label: "Permissions", path: "/permissions" },
  { label: "Inventory", path: "/inventory" },
  { label: "Transfers", path: "/transfers" },
];

const BranchSubNav = ({ branch }: BranchSubNavProps) => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const base = `/setup/branches/${id}`;

  const isActive = (path: string) => {
    if (path === "") return location.pathname === base;
    return location.pathname.startsWith(base + path);
  };

  return (
    <div className="mb-8 border-b border-gray-200 bg-white">
      <div className="mx-auto max-w-7xl">
        <div className="flex items-center gap-3 py-3 border-b border-gray-100">
          <Link
            to="/setup/branches"
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>All Branches</span>
          </Link>

          {branch && (
            <>
              <span className="text-gray-300">/</span>
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-500" />
                <span className="font-medium text-gray-900">{branch.name}</span>
                <span className="text-xs text-gray-400 font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                  {branch.code}
                </span>
                {branch.isMain && (
                  <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                    <Star className="w-3 h-3" />
                    Main
                  </span>
                )}
                {branch.status === "INACTIVE" && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                    Inactive
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        <nav className="-mb-px flex gap-1 overflow-x-auto">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={base + item.path}
              className={cn(
                "px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors",
                isActive(item.path)
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300"
              )}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default BranchSubNav;
